import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../config';
import './AdminWebhookEvents.css';

interface WebhookEvent {
  id: number;
  object_type: string;
  object_id: number;
  aspect_type: string;
  owner_id: number;
  status: string;
  attempts: number;
  last_error: string | null;
  task_enqueued: boolean;
  created_at: string | null;
  processed_at: string | null;
  user_id: number | null;
  username: string | null;
  user_name: string | null;
}

interface WhoopWebhookEvent {
  id: number;
  whoop_user_id: string;
  event_type: string;
  event_id: string | null;
  status: string;
  last_error: string | null;
  created_at: string | null;
  processed_at: string | null;
  user_id: number | null;
  username: string | null;
  user_name: string | null;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
  pages: number;
}

interface Stats {
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  last_24h: number;
  total: number;
}

const AdminWebhookEvents: React.FC = () => {
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, per_page: 50, total: 0, pages: 0 });
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Whoop state
  const [whoopEvents, setWhoopEvents] = useState<WhoopWebhookEvent[]>([]);
  const [whoopPagination, setWhoopPagination] = useState<Pagination>({ page: 1, per_page: 50, total: 0, pages: 0 });
  const [whoopStats, setWhoopStats] = useState<Stats | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  const fetchWhoopEvents = async (page: number = 1) => {
    try {
      const params = new URLSearchParams({ page: page.toString(), per_page: '50' });
      const response = await fetch(`${API_BASE_URL}/api/admin/whoop-webhook-events?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWhoopEvents(data.events);
        setWhoopPagination(data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch Whoop events:', err);
    }
  };

  const fetchWhoopStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/whoop-webhook-events/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWhoopStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch Whoop stats:', err);
    }
  };

  const fetchEvents = async (page: number = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), per_page: '50' });
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('object_type', typeFilter);

      const response = await fetch(`${API_BASE_URL}/api/admin/webhook-events?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events);
        setPagination(data.pagination);
      } else {
        setError('Błąd pobierania zdarzeń webhook');
      }
    } catch (err) {
      setError('Błąd sieci');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/webhook-events/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchStats();
    fetchWhoopEvents();
    fetchWhoopStats();
  }, []);

  useEffect(() => {
    fetchEvents(1);
  }, [statusFilter, typeFilter]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed': return 'badge-success';
      case 'pending': return 'badge-warning';
      case 'processing': return 'badge-info';
      case 'failed': return 'badge-danger';
      case 'permanently_failed': return 'badge-danger';
      case 'skipped': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading && events.length === 0) {
    return <div className="card">Ładowanie zdarzeń webhook...</div>;
  }

  if (error) {
    return <div className="card" style={{ color: 'red' }}>{error}</div>;
  }

  return (
    <div className="webhook-events-container">
      <div className="card">
        <h2>Strava Webhook Events</h2>

        {/* Stats Summary */}
        {stats && (
          <div className="webhook-stats">
            <div className="stat-card">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{stats.last_24h}</span>
              <span className="stat-label">Last 24h</span>
            </div>
            <div className="stat-card stat-success">
              <span className="stat-value">{stats.by_status.completed || 0}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-card stat-warning">
              <span className="stat-value">{stats.by_status.pending || 0}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-card stat-danger">
              <span className="stat-value">{(stats.by_status.failed || 0) + (stats.by_status.permanently_failed || 0)}</span>
              <span className="stat-label">Failed</span>
            </div>
            {(stats.by_status.permanently_failed || 0) > 0 && (
              <div className="stat-card stat-danger" onClick={() => setStatusFilter('permanently_failed')} style={{ cursor: 'pointer' }}>
                <span className="stat-value">{stats.by_status.permanently_failed}</span>
                <span className="stat-label">Perm. Failed</span>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="webhook-filters">
          <div className="filter-group">
            <label>Status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="not_completed">Not Completed</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="skipped">Skipped</option>
              <option value="failed">Failed</option>
              <option value="permanently_failed">Permanently Failed</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Type:</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All</option>
              <option value="activity">Activity</option>
              <option value="athlete">Athlete</option>
            </select>
          </div>
          <button className="btn-refresh" onClick={() => { fetchEvents(pagination.page); fetchStats(); }}>
            Refresh
          </button>
        </div>

        {/* Events Table */}
        <div className="table-container">
          <table className="webhook-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Object ID</th>
                <th>Action</th>
                <th>User</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Created</th>
                <th>Processed</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.id}>
                  <td className="id-cell">{event.id}</td>
                  <td>
                    <span className={`type-badge ${event.object_type === 'activity' ? 'type-activity' : 'type-athlete'}`}>
                      {event.object_type}
                    </span>
                  </td>
                  <td className="object-id-cell">{event.object_id}</td>
                  <td>
                    <span className={`action-badge action-${event.aspect_type}`}>
                      {event.aspect_type}
                    </span>
                  </td>
                  <td className="user-cell">
                    {event.user_name ? (
                      <span title={`User ID: ${event.user_id}, Strava ID: ${event.owner_id}`}>
                        {event.user_name}
                      </span>
                    ) : (
                      <span className="unknown-user" title={`Strava Owner ID: ${event.owner_id}`}>
                        Unknown ({event.owner_id})
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                      {event.status}
                    </span>
                  </td>
                  <td className="attempts-cell">{event.attempts}</td>
                  <td className="date-cell">{formatDate(event.created_at)}</td>
                  <td className="date-cell">{formatDate(event.processed_at)}</td>
                  <td className="error-cell">
                    {event.last_error && (
                      <span className="error-text" title={event.last_error}>
                        {event.last_error.length > 50 ? event.last_error.substring(0, 50) + '...' : event.last_error}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="pagination">
            <button
              disabled={pagination.page <= 1}
              onClick={() => fetchEvents(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="page-info">
              Page {pagination.page} of {pagination.pages} ({pagination.total} total)
            </span>
            <button
              disabled={pagination.page >= pagination.pages}
              onClick={() => fetchEvents(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* WHOOP Webhook Events */}
      <div className="card" style={{ marginTop: '24px' }}>
        <h2>WHOOP Webhook Events</h2>

        {whoopStats && (
          <div className="webhook-stats">
            <div className="stat-card">
              <span className="stat-value">{whoopStats.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{whoopStats.last_24h}</span>
              <span className="stat-label">Last 24h</span>
            </div>
            <div className="stat-card stat-success">
              <span className="stat-value">{whoopStats.by_status.completed || 0}</span>
              <span className="stat-label">Completed</span>
            </div>
            <div className="stat-card stat-danger">
              <span className="stat-value">{whoopStats.by_status.failed || 0}</span>
              <span className="stat-label">Failed</span>
            </div>
            {whoopStats.by_type && Object.keys(whoopStats.by_type).length > 0 && (
              <div className="stat-card">
                <span className="stat-value" style={{ fontSize: '12px' }}>
                  {Object.entries(whoopStats.by_type).map(([t, c]) => `${t}: ${c}`).join(', ')}
                </span>
                <span className="stat-label">By Type</span>
              </div>
            )}
          </div>
        )}

        <div className="webhook-filters">
          <button className="btn-refresh" onClick={() => { fetchWhoopEvents(whoopPagination.page); fetchWhoopStats(); }}>
            Refresh
          </button>
        </div>

        <div className="table-container">
          <table className="webhook-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Event Type</th>
                <th>Event ID</th>
                <th>User</th>
                <th>Status</th>
                <th>Created</th>
                <th>Processed</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {whoopEvents.map(event => (
                <tr key={event.id}>
                  <td className="id-cell">{event.id}</td>
                  <td>
                    <span className={`type-badge ${event.event_type.startsWith('recovery') ? 'type-activity' : 'type-athlete'}`}>
                      {event.event_type}
                    </span>
                  </td>
                  <td className="object-id-cell" style={{ fontSize: '11px' }}>{event.event_id || '-'}</td>
                  <td className="user-cell">
                    {event.user_name || event.username || (
                      <span className="unknown-user">Whoop #{event.whoop_user_id}</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                      {event.status}
                    </span>
                  </td>
                  <td className="date-cell">{formatDate(event.created_at)}</td>
                  <td className="date-cell">{formatDate(event.processed_at)}</td>
                  <td className="error-cell">
                    {event.last_error && (
                      <span className="error-text" title={event.last_error}>
                        {event.last_error.length > 50 ? event.last_error.substring(0, 50) + '...' : event.last_error}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {whoopEvents.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: '#888', padding: '20px' }}>
                    No WHOOP webhook events yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {whoopPagination.pages > 1 && (
          <div className="pagination">
            <button
              disabled={whoopPagination.page <= 1}
              onClick={() => fetchWhoopEvents(whoopPagination.page - 1)}
            >
              Previous
            </button>
            <span className="page-info">
              Page {whoopPagination.page} of {whoopPagination.pages} ({whoopPagination.total} total)
            </span>
            <button
              disabled={whoopPagination.page >= whoopPagination.pages}
              onClick={() => fetchWhoopEvents(whoopPagination.page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWebhookEvents;
